// src/app/layout.ts
import { sendAI } from '../api/ai.api';
import { PUBLIC_ROUTES } from '../constants/constantsData';
import { getCurrentRole, getUserLogin } from '../utils/common';
import { renderSidebarMenu } from '../layouts/sidebar-handler';
import { requestApi, logoutSharedSession } from '../core/api-client';
import { showToast } from '../utils/toast';
import { OtpPopup } from '../pages/components/otp-popup';
// Biến lưu trạng thái đã tải layout hay chưa
let isLayoutLoaded = false;
const escapeHtml = (str: string) =>
  (str || '').toString().replace(/[&<>"']/g, (m) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  }[m] || m));
const formatDateVi = (isoStr?: string | null) => {
  if (!isoStr) return '-';
  const d = new Date(isoStr);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};
export async function loadLayout(path: string) {
  
  const currentRole = getCurrentRole();

  // Load CSS tương ứng với role
  await loadRoleCss(currentRole);
  const app = document.querySelector('#app')!;

  // 1. Trang login
  if (PUBLIC_ROUTES.some(route => route === path)) {
    app.innerHTML = `<div id="content"></div>`;
    isLayoutLoaded = false;
    return;
  }

  // 2. Nếu layout đã load thì không load lại
  if (
    isLayoutLoaded &&
    document.querySelector('#header')?.children.length
  ) {
    return;
  }


  switch (currentRole) {
    case 'admin':
    case 'branch admin':
      await loadLayoutAdmin();
      break;

    case 'user':
      await loadLayoutUser();
      break;

    default:
      await loadLayoutAdmin();
      break;
  }
  initAccountProfileFeature();

  isLayoutLoaded = true;
}

// Hàm khởi tạo sự kiện tìm kiếm menu user sidebar
function initUserSidebarSearch(): void {
  const searchInput = document.querySelector('#menu-search-input') as HTMLInputElement;
  const sideItems = document.querySelectorAll('.side-item') as NodeListOf<HTMLElement>;

  if (!searchInput) return;

  searchInput.addEventListener('input', (e) => {
    const keyword = (e.target as HTMLInputElement).value.toLowerCase().trim();

    sideItems.forEach((item) => {
      const textName = item.getAttribute('data-name')?.toLowerCase() || item.textContent?.toLowerCase() || '';
      
      if (textName.includes(keyword)) {
        item.style.display = 'flex'; // Hiển thị lại nếu khớp từ khóa
      } else {
        item.style.display = 'none'; // Ẩn đi nếu không khớp
      }
    });
  });
}
// Hàm khởi tạo sự kiện toggle sidebar từ Header và tìm kiếm menu
function initUserSidebarToggle(): void {
  const sidebar = document.querySelector('#sidebar') as HTMLElement;
  const toggleBtn = document.querySelector('.topbar-left .burger') as HTMLElement;
  const searchInput = document.querySelector('#menu-search-input') as HTMLInputElement;
  const sideItems = document.querySelectorAll('.side-item') as NodeListOf<HTMLElement>;

  if (!sidebar || !toggleBtn) return;

  // 1. Sự kiện click vào nút burger trên Header để thu nhỏ / mở rộng sidebar
  toggleBtn.addEventListener('click', () => {
    sidebar.classList.toggle('collapsed');

    // Đổi icon qua lại để người dùng dễ nhận biết
    if (sidebar.classList.contains('collapsed')) {
      toggleBtn.innerHTML = '➔'; // Mũi tên mở rộng
      toggleBtn.setAttribute('title', 'Mở rộng menu');
    } else {
      toggleBtn.innerHTML = '☰'; // Icon hamburger ban đầu
      toggleBtn.setAttribute('title', 'Thu nhỏ menu');
    }
  });

  // 2. Sự kiện tìm kiếm lọc danh sách menu trong sidebar
  if (searchInput) {
    searchInput.addEventListener('input', (e) => {
      const keyword = (e.target as HTMLInputElement).value.toLowerCase().trim();

      sideItems.forEach((item) => {
        const textName = item.getAttribute('data-name')?.toLowerCase() || item.textContent?.toLowerCase() || '';
        
        if (textName.includes(keyword)) {
          item.style.display = 'flex';
        } else {
          item.style.display = 'none';
        }
      });
    });
  }
}

// Hàm quản lý load layout user chính
async function loadLayoutUser(app: HTMLElement = document.querySelector('#app')!) {
  app.innerHTML = await fetch('/src/layouts/user-layout.html')
    .then(r => r.text());

  document.querySelector('#header')!.innerHTML = await fetch(
    '/src/layouts/user-header.html'
  ).then(r => r.text());

  document.querySelector('#sidebar')!.innerHTML = await fetch(
    '/src/layouts/user-sidebar.html'
  ).then(r => r.text());

  document.querySelector('#footer')!.innerHTML = await fetch(
    '/src/layouts/user-footer.html'
  ).then(r => r.text());
  
  renderSidebarMenu('user');

  // Khởi tạo các event/hàm logic riêng của user layout ở đây
  initUserSidebarSearch();
  initUserSidebarToggle();
  initUserProfileToggle();
  initNotification();
}
function initNotification(){
  // -------------------------------------------------------------
  // XỬ LÝ HỆ THỐNG THÔNG BÁO (NOTIFICATION DROPDOWN & LOAD MORE)
  // -------------------------------------------------------------
  const notifBellToggle = document.getElementById('notifBellToggle');
  const notifDropdown = document.getElementById('notifDropdown');
  const notifBadge = document.getElementById('notifBadge');
  const notifListContainer = document.getElementById('notifListContainer');
  const notifFooter = document.getElementById('notifFooter');
  const btnMarkAllRead = document.getElementById('btnMarkAllRead');

  let notifSkipCount = 0;
  const notifMaxResultCount = 10;
  let notifTotalCount = 0;
  let isNotifLoaded = false;

  // 1. Lấy tổng số chưa đọc để hiển thị Badge
  const fetchUnreadCount = async () => {
    try {
      const res = await requestApi('/api/app/notification-user/unread-count', 'GET');
      const count = typeof res === 'number' ? res : (res?.result ?? res?.unreadCount ?? 0);
      if (count > 0) {
        if (notifBadge) {
          notifBadge.textContent = count > 99 ? '99+' : count.toString();
          notifBadge.style.display = 'block';
        }
      } else {
        if (notifBadge) notifBadge.style.display = 'none';
      }
    } catch (e) {
      console.error('Lỗi lấy số lượng thông báo chưa đọc:', e);
    }
  };

  // 2. Lấy danh sách thông báo phân trang (Load more)
  const fetchNotifications = async (isLoadMore = false) => {
    try {
      if (!isLoadMore) {
        notifSkipCount = 0;
        if (notifListContainer) notifListContainer.innerHTML = '<div style="padding: 20px; text-align: center; color: #6c757d; font-size: 13px;"><i class="fas fa-spinner fa-spin"></i> Đang tải...</div>';
      }

      const url = `/api/app/notification-user/by-customer-paged?SkipCount=${notifSkipCount}&MaxResultCount=${notifMaxResultCount}`;
      const res = await requestApi(url, 'GET');
      const resultData = res?.result || res;
      
      notifTotalCount = resultData?.totalCount || 0;
      const items = resultData?.items || [];

      if (!isLoadMore && notifListContainer) {
        notifListContainer.innerHTML = '';
      }

      if (items.length === 0 && !isLoadMore) {
        if (notifListContainer) {
          notifListContainer.innerHTML = '<div style="padding: 30px; text-align: center; color: #6c757d; font-size: 13px;">Không có thông báo nào</div>';
        }
        if (notifFooter) notifFooter.style.display = 'none';
        return;
      }

      items.forEach((item: any) => {
        const isRead = item.isRead;
        const bgStyle = isRead ? '#fff' : '#f0fdf4'; // Chưa đọc nền xanh nhạt, đã đọc nền trắng
        const dotDisplay = isRead ? 'none' : 'inline-block';
        const timeStr = formatDateVi(item.creationTime || item.sendTime);

        const rowEl = document.createElement('div');
        rowEl.className = 'notif-item';
        rowEl.style.cssText = `padding: 12px 16px; border-bottom: 1px solid #f1f3f5; background: ${bgStyle}; cursor: pointer; transition: background 0.15s; display: flex; gap: 10px; align-items: flex-start;`;
        
        rowEl.innerHTML = `
          <div style="margin-top: 4px;">
            <span style="width: 8px; height: 8px; background: #11A75D; border-radius: 50%; display: ${dotDisplay};"></span>
          </div>
          <div style="flex: 1; min-width: 0;">
            <div style="font-weight: ${isRead ? '500' : '600'}; font-size: 13px; color: #212529; margin-bottom: 2px;">${escapeHtml(item.title)}</div>
            <div style="font-size: 12px; color: #495057; line-height: 1.4; margin-bottom: 4px; word-break: break-word;">${escapeHtml(item.content)}</div>
            <div style="font-size: 11px; color: #868e96;">${timeStr}</div>
          </div>
        `;

        // Sự kiện click vào dòng: Đánh dấu đã đọc 1 tin nếu chưa đọc
        rowEl.onclick = async () => {
          if (!item.isRead) {
            try {
              await requestApi(`/api/app/notification-user/${item.id}/mark-as-read`, 'POST');
              item.isRead = true;
              rowEl.style.background = '#fff';
              const dot = rowEl.querySelector('span[style*="border-radius: 50%"]') as HTMLElement;
              if (dot) dot.style.display = 'none';
              const titleEl = rowEl.querySelector('div[style*="font-weight"]') as HTMLElement;
              if (titleEl) titleEl.style.fontWeight = '500';
              fetchUnreadCount(); // Cập nhật lại badge
            } catch (err) {
              console.error('Lỗi đánh dấu đã đọc:', err);
            }
          }
        };

        notifListContainer?.appendChild(rowEl);
      });

      notifSkipCount += items.length;

      // Hiển thị hoặc ẩn nút Load more
      if (notifFooter) {
        if (notifSkipCount < notifTotalCount) {
          notifFooter.style.display = 'block';
        } else {
          notifFooter.style.display = 'none';
        }
      }

    } catch (err) {
      console.error('Lỗi tải danh sách thông báo:', err);
      if (!isLoadMore && notifListContainer) {
        notifListContainer.innerHTML = '<div style="padding: 20px; text-align: center; color: #dc3545; font-size: 13px;">Lỗi tải dữ liệu thông báo</div>';
      }
    }
  };

  // 3. Sự kiện bấm vào icon Chuông để mở/đóng dropdown
  if (notifBellToggle && notifDropdown) {
    notifBellToggle.onclick = (e) => {
      e.stopPropagation();
      const isVisible = notifDropdown.style.display === 'block';
      notifDropdown.style.display = isVisible ? 'none' : 'block';

      if (!isVisible) {
        fetchNotifications(false); // Load trang đầu tiên khi mở
        // Đóng dropdown profile nếu đang mở
        const profileDropdown = document.getElementById('profileDropdown');
        if (profileDropdown) profileDropdown.style.display = 'none';
      }
    };
  }

  // Sự kiện Load More
  if (notifFooter) {
    notifFooter.onclick = () => {
      fetchNotifications(true);
    };
  }

  // 4. Đánh dấu tất cả là đã đọc
  if (btnMarkAllRead) {
    btnMarkAllRead.onclick = async (e) => {
      e.stopPropagation();
      try {
        await requestApi('/api/app/notification-user/mark-all-as-read', 'POST');
        showToast('success', 'Đã đánh dấu tất cả thông báo là đã đọc');
        fetchUnreadCount();
        fetchNotifications(false); // Tải lại danh sách để reset trạng thái UI
      } catch (err) {
        showToast('error', 'Có lỗi xảy ra!');
        console.error(err);
      }
    };
  }

  // Click ra ngoài thì tự động ẩn dropdown thông báo
  document.addEventListener('click', (e) => {
    const container = document.getElementById('notifDropdownContainer');
    if (container && notifDropdown && !container.contains(e.target as Node)) {
      notifDropdown.style.display = 'none';
    }
  });

  // Gọi lần đầu khi trang khởi tạo để hiển thị số badge chưa đọc
  fetchUnreadCount();
}
function initUserProfileToggle(){
  var user = getUserLogin();
  // Lấy thông tin user (ví dụ từ API hoặc localStorage)

  const displayName = user?.fullName ?? user?.userName ?? 'Người dùng';

  // Cập nhật tên trên user-chip (bảo toàn icon mũi tên và avatar)
  const userChipSpan = document.querySelector('#userChipToggle span');
  if (userChipSpan) {
    userChipSpan.innerHTML = `${displayName} <i class="fas fa-chevron-down chevron" style="font-size: 10px; color: #adb5bd; transition: transform 0.2s;"></i>`;
  }

  // Cập nhật chữ cái đầu cho avatar (nếu muốn đồng bộ tự động)
  const avatarEl = document.querySelector('#userChipToggle .avatar');
  if (avatarEl && displayName) {
    const initials = displayName.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2);
    avatarEl.textContent = initials;
  }
  // -------------------------------------------------------------
  // XỬ LÝ MENU DROPDOWN PROFILE (USER CHIP)
  // -------------------------------------------------------------
  const userChipToggle = document.getElementById('userChipToggle');
  const profileDropdown = document.getElementById('profileDropdown');

  if (userChipToggle && profileDropdown) {
    // Click vào user chip để bật/tắt menu
    userChipToggle.onclick = (e) => {
      e.stopPropagation();
      const isVisible = profileDropdown.style.display === 'block';
      profileDropdown.style.display = isVisible ? 'none' : 'block';
    };

    // Click ra bên ngoài thì tự động ẩn menu
    document.addEventListener('click', (e) => {
      const container = document.getElementById('profileDropdownContainer');
      if (container && !container.contains(e.target as Node)) {
        profileDropdown.style.display = 'none';
      }
    });
  }

  // Xử lý sự kiện nút Đăng xuất (Ví dụ gọi API logout hoặc clear token)
  const logoutButton = document.getElementById('logoutButton');
  if (logoutButton) {
    logoutButton.onclick = async (e) => {
      e.preventDefault();
      // Thực hiện xóa token, thông tin user và chuyển hướng về trang đăng nhập
      localStorage.clear();
      await logoutSharedSession();
      window.location.href = '/#/login'; // Thay đổi đường dẫn trang đăng nhập của bạn nếu cần
    };
  }
}
async function loadRoleCss(role: string) {
  switch (role) {
    case 'admin':
    case 'branch admin':
      await import('../assets/app.css');
      break;

    case 'user':
      await import('../assets/user.css');
      break;

    default:
      await import('../assets/app.css');
      break;
  }
}

// ============================================================
// ================== LAYOUT ADMIN ==============================
// ============================================================
async function loadLayoutAdmin(app: HTMLElement = document.querySelector('#app')!) {
  // 3. Tải khung layout lần đầu tiên khi truy cập trang quản trị
  app.innerHTML = await fetch('/src/layouts/layout.html').then((r) => r.text());

  document.querySelector('#header')!.innerHTML = await fetch(
    '/src/layouts/header.html'
  ).then((r) => r.text());

  document.querySelector('#sidebar')!.innerHTML = await fetch(
    '/src/layouts/sidebar.html'
  ).then((r) => r.text());

  // 🚀 GỌI HÀM RENDER MENU TỪ LOCALSTORAGE TẠI ĐÂY
  renderSidebarMenu();

  document.querySelector('#footer')!.innerHTML = await fetch(
    '/src/layouts/footer.html'
  ).then((r) => r.text());

  initAdminSidebarDropdown();
  initAdminLayout();
  initAdminEvents();
  initNotification();
  const user = getUserLogin();

  const nameEl = document.querySelector<HTMLElement>(
      '#userInfo .user-name'
  );
  const helloEl = document.querySelector<HTMLElement>(
      '#helloUser'
  );
  
  if (helloEl) {
      helloEl.textContent = `Xin chào, ${user?.fullName ?? user?.userName} 👋`;;
  }
  if (nameEl) {
      nameEl.textContent = user?.fullName ?? user?.userName;
  }
  const sidebarProfileNameEl = document.querySelector<HTMLElement>(
      '.sidebar-profile-info .sidebar-profile-name'
  );
  const sidebarProfileRoleEl = document.querySelector<HTMLElement>(
      '.sidebar-profile-info .sidebar-profile-role'
  );
  if (sidebarProfileNameEl) {
      sidebarProfileNameEl.textContent = user?.fullName ?? user?.userName;
  }
  if (sidebarProfileRoleEl) {
      sidebarProfileRoleEl.textContent = user?.currentRole ?? '';
  }
}

function initAdminLayout() {
  // ===== Profile dropdown =====
  document.addEventListener('click', (event) => {
    const dropdown = document.getElementById('profileDropdown');
    const userContainer = document.querySelector('.profile-dropdown-container');

    if (
      dropdown &&
      userContainer &&
      dropdown.classList.contains('active') &&
      !userContainer.contains(event.target as Node)
    ) {
      dropdown.classList.remove('active');
    }
  });
}
function initAdminEvents() {
  const sidebar = document.getElementById('sidebar');

  // 1. Khôi phục trạng thái thu gọn từ localStorage khi vừa khởi tạo
  if (sidebar && localStorage.getItem('sidebarCollapsed') === 'true') {
    sidebar.classList.add('collapsed');
  }

  // 2. Hàm Toggle & lưu trạng thái mới
  const toggleSidebar = () => {
    if (!sidebar) return;
    sidebar.classList.toggle('collapsed');
    const isCollapsed = sidebar.classList.contains('collapsed');
    localStorage.setItem('sidebarCollapsed', String(isCollapsed));
  };

  // 3. Các sự kiện AI Chat
  document.getElementById('btnAIChat')?.addEventListener('click', toggleAdminAIChat);
  document.getElementById('btnSendAI')?.addEventListener('click', sendAdminAIMessage);

  const input = document.getElementById('chatInput') as HTMLInputElement;
  input?.addEventListener('keydown', (e: KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendAdminAIMessage();
    }
  });

  // 4. Sự kiện Mobile Sidebar
  document.getElementById('btnMenuToggle')?.addEventListener('click', toggleAdminMobileSidebar);
  document.getElementById('sidebarOverlay')?.addEventListener('click', toggleAdminMobileSidebar);

  // 🚀 5. Sự kiện Toggle Sidebar Desktop (bật/tắt & lưu localStorage)
  document.getElementById('btnSidebarToggle')?.addEventListener('click', toggleSidebar);
  document.getElementById('btnSidebarExpand')?.addEventListener('click', toggleSidebar);

  // 6. Các sự kiện hệ thống khác
  document.getElementById('menuSearchInput')?.addEventListener('input', filterAdminMenu);
  document.getElementById('userInfo')?.addEventListener('click', toggleAdminProfileDropdown);
  document.getElementById('logoutButton')?.addEventListener('click', handleAdminLogout);
}
export function highlightCurrentMenu(path: string) {
   var currentRole = getCurrentRole();
   switch (currentRole) {
     case 'admin':
     case 'branch admin':
       highlightCurrentAdminMenu(path);
       break;
     case 'user':
       highlightCurrentUserMenu(path);
       break;
   }
}

export function highlightCurrentUserMenu(path: string) {
  path = path.replace(/\/+$/, '').toLowerCase();

  document.querySelectorAll('#sidebar .active').forEach((x) => x.classList.remove('active'));
  document.querySelectorAll('#sidebar .open').forEach((x) => x.classList.remove('open'));

  const links = Array.from(document.querySelectorAll<HTMLAnchorElement>('#sidebar a[href]'));

  const current = links.find((a) => {
    const href = (a.getAttribute('href') ?? '').replace(/\/+$/, '').toLowerCase();
    return href === path;
  });

  if (!current) return;

  current.classList.add('active');
  console.log(current,path)
  var menu = (JSON.parse(localStorage.getItem('user') ?? '{}'))?.menus?.find(a=> a.path == path);
  document.getElementById('nav-links').innerHTML = `<a href="${path}"><span class="ic" style="
    margin-right: 5px;
    font-size: 18px;
">${menu?.icon}</span>${menu?.title}</a>`
}

export function highlightCurrentAdminMenu(path: string) {
  path = path.replace(/\/+$/, '').toLowerCase();

  document.querySelectorAll('#sidebarMenu .active').forEach((x) => x.classList.remove('active'));
  document.querySelectorAll('#sidebarMenu .open').forEach((x) => x.classList.remove('open'));

  const links = Array.from(document.querySelectorAll<HTMLAnchorElement>('#sidebarMenu a[href]'));

  const current = links.find((a) => {
    const href = (a.getAttribute('href') ?? '').replace(/\/+$/, '').toLowerCase();
    return href === path;
  });

  if (!current) return;

  current.classList.add('active');

  let parent = current.parentElement;
  while (parent) {
    const menuItem = parent.closest('.menu-item');
    if (!menuItem) break;

    if (menuItem.classList.contains('has-submenu')) {
      menuItem.classList.add('open');
    }

    parent = menuItem.parentElement?.closest('.menu-item') ?? null;
  }
}

export function toggleAdminMobileSidebar() {
  document.getElementById('sidebar')?.classList.toggle('mobile-open');
  document.getElementById('sidebarOverlay')?.classList.toggle('active');
}

export function toggleAdminProfileDropdown(event: MouseEvent) {
  event.stopPropagation();
  document.getElementById('profileDropdown')?.classList.toggle('active');
}

export function filterAdminMenu() {
  const input = document.getElementById('menuSearchInput') as HTMLInputElement;
  if (!input) return;

  const filter = input.value.toLowerCase();
  const ul = document.getElementById('sidebarMenu');
  if (!ul) return;

  const menuItems = ul.getElementsByClassName('menu-item');

  for (let i = 0; i < menuItems.length; i++) {
    const item = menuItems[i] as HTMLElement;
    const link = item.querySelector('.menu-link');
    if (!link) continue;

    let hasMatch = (link.textContent ?? '').toLowerCase().includes(filter);
    const submenu = item.querySelector('.submenu');

    if (submenu) {
      const subItems = submenu.getElementsByClassName('submenu-link');
      let childMatch = false;

      for (let j = 0; j < subItems.length; j++) {
        if ((subItems[j].textContent ?? '').toLowerCase().includes(filter)) {
          childMatch = true;
          break;
        }
      }

      if (childMatch) {
        hasMatch = true;
        if (filter) item.classList.add('open');
      }
    }

    item.style.display = hasMatch ? '' : 'none';
  }
}

export function toggleAdminAIChat() {
  document.getElementById('aiChatWindow')?.classList.toggle('show');
}

export async function sendAdminAIMessage() {
  const input = document.getElementById('chatInput') as HTMLInputElement;
  const messages = document.getElementById('chatMessages') as HTMLElement;
  const text = input.value.trim();

  if (!text) return;

  messages.insertAdjacentHTML('beforeend', `<div class="user-message">${text}</div>`);
  input.value = '';
  messages.scrollTop = messages.scrollHeight;

  try {
    const aiResponse = await sendAI(text);
    messages.insertAdjacentHTML('beforeend', `<div class="ai-message">${aiResponse}</div>`);
  } catch {
    messages.insertAdjacentHTML('beforeend', `<div class="ai-message ai-error-message">Lỗi kết nối AI.</div>`);
  }

  messages.scrollTop = messages.scrollHeight;
}

function getAdminBreadcrumb(path: string): string {
  const links = document.querySelectorAll<HTMLAnchorElement>('a[href]');
  let breadcrumb: string[] = [];

  links.forEach((link) => {
    if (link.getAttribute('href') !== path) return;

    const result: string[] = [];
    let li: HTMLLIElement | null = link.closest('li');

    while (li) {
      const a = li.querySelector(':scope > a');
      if (a?.textContent?.trim()) {
        result.unshift(a.textContent.trim());
      }
      li = li.parentElement?.closest('li') ?? null;
    }

    breadcrumb = result;
  });

  return breadcrumb.length ? breadcrumb.join(' > ') : 'Trang chủ';
}

export function updateBreadcrumb(path: string) {
  var currentRole = getCurrentRole();
  switch (currentRole) {
    case 'admin':
    case 'branch admin':
      updateAdminBreadcrumb(path);
      break;
  }
}
export function updateAdminBreadcrumb(path: string) {
  const breadcrumb = document.querySelector('.header-breadcrumb');
  if (!breadcrumb) return;

  breadcrumb.innerHTML = `
      <a href="/" style="color: #6b7280;"><i class="fa-solid fa-house"></i></a>
      ${getAdminBreadcrumb(path)}
  `;
}

export function initAdminSidebarDropdown() {
  const sidebarMenu = document.getElementById('sidebarMenu');
  if (!sidebarMenu) return;

  // Sử dụng ủy quyền sự kiện (Event Delegation) trực tiếp trên sidebarMenu
  // Bỏ hoàn toàn các điều kiện kiểm tra dataset để đảm bảo luôn bắt được sự kiện click
  sidebarMenu.onclick = (e: MouseEvent) => {
    const target = e.target as HTMLElement;
    const link = target.closest('.has-submenu > a.menu-link');
    
    if (!link) return;
    
    e.preventDefault();
    e.stopPropagation();

    const parentLi = link.parentElement;
    if (parentLi) {
      parentLi.classList.toggle('open');
    }
  };
}
export async function handleAdminLogout(e: Event) {
  e.preventDefault();

  // 1. Xóa accessToken
  localStorage.removeItem('accessToken');
  await logoutSharedSession();
  // (Tùy chọn) Xóa thêm thông tin user nếu bạn có lưu
  // localStorage.removeItem('user');

  // 2. Ẩn dropdown menu đi cho gọn
  document.getElementById('profileDropdown')?.classList.remove('active');

  // 3. Chuyển hướng về trang đăng nhập
  window.location.hash = '#/login';
}

export function initAccountProfileFeature() {
  let cachedUserEmail = '';

  // Hàm helper sinh HTML động cho từng Modal
  const getModalHtml = (modalId: string) => {
    switch (modalId) {
      case 'accountProfileModal':
        return `
          <div class="app-modal-overlay" id="accountProfileModal" style="z-index: 2050; display: none; align-items: center; justify-content: center;">
            <div class="app-modal-card" style="max-width: 550px; width: 100%; background: #fff; padding: 20px; border-radius: 8px; box-shadow: 0 5px 20px rgba(0,0,0,0.15);">
              <div class="app-modal-header" style="border-bottom: 1px solid #e9ecef; padding-bottom: 12px; margin-bottom: 16px; display: flex; justify-content: space-between; align-items: center;">
                <h3 class="app-modal-title" style="font-size: 18px; font-weight: 600; color: #2c3e50; margin: 0;">Thông tin tài khoản</h3>
                <button id="btnCloseProfileModal" type="button" class="app-modal-close" style="background: none; border: none; font-size: 18px; color: #6c757d; cursor: pointer;"><i class="fas fa-times"></i></button>
              </div>
              
              <div class="app-modal-body" style="display: flex; flex-direction: column; gap: 16px;">
                <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 16px;">
                  <div class="form-group">
                    <label class="form-label" style="font-size: 12px; font-weight: 500; display: block; margin-bottom: 6px;">Họ</label>
                    <input type="text" id="profFirstName" class="form-input" style="width: 100%; padding: 8px 12px; border: 1px solid #ced4da; border-radius: 4px; box-sizing: border-box;" placeholder="Nhập họ..." />
                  </div>
                  <div class="form-group">
                    <label class="form-label" style="font-size: 12px; font-weight: 500; display: block; margin-bottom: 6px;">Tên</label>
                    <input type="text" id="profLastName" class="form-input" style="width: 100%; padding: 8px 12px; border: 1px solid #ced4da; border-radius: 4px; box-sizing: border-box;" placeholder="Nhập tên..." />
                  </div>
                </div>
                
                <div class="form-group">
                  <label class="form-label" style="font-size: 12px; font-weight: 500; display: block; margin-bottom: 6px;">Tên đăng nhập</label>
                  <input type="text" id="profUsername" class="form-input" style="width: 100%; padding: 8px 12px; border: 1px solid #ced4da; border-radius: 4px; background: #fff; color: #212529; box-sizing: border-box;" placeholder="Nhập tên đăng nhập..." />
                </div>
                
                <div class="form-group">
                  <label class="form-label" style="font-size: 12px; font-weight: 500; display: block; margin-bottom: 6px;">Điện thoại</label>
                  <div style="display: flex; gap: 8px;">
                    <input type="text" id="profPhone" class="form-input" style="flex: 1; padding: 8px 12px; border: 1px solid #ced4da; border-radius: 4px; background: #e9ecef; color: #495057; box-sizing: border-box;" readonly />
                    <button type="button" id="btnEditProfPhone" class="btn btn-outline" style="padding: 6px 12px; font-size: 13px; border: 1px solid #ced4da; border-radius: 4px; background: #fff; cursor: pointer;"><i class="fas fa-pen" style="margin-right: 4px; color: #0d6efd;"></i> Sửa</button>
                  </div>
                </div>
                
                <div class="form-group">
                  <label class="form-label" style="font-size: 12px; font-weight: 500; display: block; margin-bottom: 6px;">Email</label>
                  <div style="display: flex; gap: 8px;">
                    <input type="email" id="profEmail" class="form-input" style="flex: 1; padding: 8px 12px; border: 1px solid #ced4da; border-radius: 4px; background: #e9ecef; color: #495057; box-sizing: border-box;" readonly />
                    <button type="button" id="btnEditProfEmail" class="btn btn-outline" style="padding: 6px 12px; font-size: 13px; border: 1px solid #ced4da; border-radius: 4px; background: #fff; cursor: pointer;"><i class="fas fa-pen" style="margin-right: 4px; color: #0d6efd;"></i> Sửa</button>
                  </div>
                </div>
              </div>
              
              <div class="app-modal-footer" style="border-top: 1px solid #e9ecef; padding-top: 16px; margin-top: 20px; display: flex; justify-content: flex-end; gap: 8px;">
                <button type="button" id="btnCancelProfile" class="btn btn-outline" style="padding: 8px 16px; border: 1px solid #ced4da; border-radius: 4px; background: #fff; cursor: pointer;">Hủy</button>
                <button type="button" id="btnOpenChangePassword" class="btn btn-outline" style="padding: 8px 16px; border: 1px solid #ced4da; border-radius: 4px; background: #fff; cursor: pointer;">Đổi mật khẩu</button>
                <button type="button" id="btnSaveProfile" class="btn btn-primary" style="background-color: #11A75D; color: #fff; border: none; padding: 8px 20px; border-radius: 4px; cursor: pointer; font-weight: 500;">Lưu</button>
              </div>
            </div>
          </div>
        `;

      case 'editPhoneModal':
        return `
          <div class="app-modal-overlay" id="editPhoneModal" style="z-index: 2060; display: none; align-items: center; justify-content: center;">
            <div class="app-modal-card" style="max-width: 400px; width: 100%; background: #fff; padding: 20px; border-radius: 8px; box-shadow: 0 5px 20px rgba(0,0,0,0.2);">
              <div class="app-modal-header" style="border-bottom: 1px solid #e9ecef; padding-bottom: 12px; margin-bottom: 16px; display: flex; justify-content: space-between; align-items: center;">
                <h3 class="app-modal-title" style="margin:0; font-size: 16px; font-weight: 600;">Cập nhật điện thoại</h3>
                <button type="button" id="btnClosePhoneModal" style="background: none; border: none; font-size: 16px; color: #6c757d; cursor: pointer;"><i class="fas fa-times"></i></button>
              </div>
              <div class="app-modal-body">
                <div class="form-group">
                  <label class="form-label" style="font-size: 12px; font-weight: 500; display: block; margin-bottom: 6px;">Số điện thoại mới</label>
                  <input type="text" id="txtNewPhone" class="form-input" style="width: 100%; padding: 8px 12px; border: 1px solid #ced4da; border-radius: 4px; box-sizing: border-box;" placeholder="Nhập số điện thoại mới..." />
                </div>
              </div>
              <div class="app-modal-footer" style="border-top: 1px solid #e9ecef; padding-top: 16px; margin-top: 20px; display: flex; justify-content: flex-end; gap: 8px;">
                <button type="button" id="btnCancelPhone" class="btn btn-outline" style="padding: 6px 16px; border: 1px solid #ced4da; border-radius: 4px; background: #fff; cursor: pointer;">Hủy</button>
                <button type="button" id="btnSavePhone" class="btn btn-primary" style="background-color: #11A75D; color: #fff; border: none; padding: 6px 16px; border-radius: 4px; cursor: pointer;">Cập nhật</button>
              </div>
            </div>
          </div>
        `;

      case 'editEmailModal':
        return `
          <div class="app-modal-overlay" id="editEmailModal" style="z-index: 2060; display: none; align-items: center; justify-content: center;">
            <div class="app-modal-card" style="max-width: 400px; width: 100%; background: #fff; padding: 20px; border-radius: 8px; box-shadow: 0 5px 20px rgba(0,0,0,0.2);">
              <div class="app-modal-header" style="border-bottom: 1px solid #e9ecef; padding-bottom: 12px; margin-bottom: 16px; display: flex; justify-content: space-between; align-items: center;">
                <h3 class="app-modal-title" style="margin:0; font-size: 16px; font-weight: 600;">Cập nhật Email</h3>
                <button type="button" id="btnCloseEmailModal" style="background: none; border: none; font-size: 16px; color: #6c757d; cursor: pointer;"><i class="fas fa-times"></i></button>
              </div>
              <div class="app-modal-body">
                <div class="form-group">
                  <label class="form-label" style="font-size: 12px; font-weight: 500; display: block; margin-bottom: 6px;">Email mới</label>
                  <input type="email" id="txtNewEmail" class="form-input" style="width: 100%; padding: 8px 12px; border: 1px solid #ced4da; border-radius: 4px; box-sizing: border-box;" placeholder="Nhập email mới..." />
                </div>
              </div>
              <div class="app-modal-footer" style="border-top: 1px solid #e9ecef; padding-top: 16px; margin-top: 20px; display: flex; justify-content: flex-end; gap: 8px;">
                <button type="button" id="btnCancelEmail" class="btn btn-outline" style="padding: 6px 16px; border: 1px solid #ced4da; border-radius: 4px; background: #fff; cursor: pointer;">Hủy</button>
                <button type="button" id="btnSaveEmail" class="btn btn-primary" style="background-color: #11A75D; color: #fff; border: none; padding: 6px 16px; border-radius: 4px; cursor: pointer;">Cập nhật</button>
              </div>
            </div>
          </div>
        `;

      case 'changePasswordModal':
        return `
          <div class="app-modal-overlay" id="changePasswordModal" style="z-index: 2060; display: none; align-items: center; justify-content: center;">
            <div class="app-modal-card" style="max-width: 400px; width: 100%; background: #fff; padding: 20px; border-radius: 8px; box-shadow: 0 5px 20px rgba(0,0,0,0.2);">
              <div class="app-modal-header" style="border-bottom: 1px solid #e9ecef; padding-bottom: 12px; margin-bottom: 16px; display: flex; justify-content: space-between; align-items: center;">
                <h3 class="app-modal-title" style="margin:0; font-size: 16px; font-weight: 600;">Đổi mật khẩu</h3>
                <button type="button" id="btnClosePasswordModal" style="background: none; border: none; font-size: 16px; color: #6c757d; cursor: pointer;"><i class="fas fa-times"></i></button>
              </div>
              <div class="app-modal-body" style="display: flex; flex-direction: column; gap: 12px;">
                
                <div class="form-group">
                  <label class="form-label" style="font-size: 12px; font-weight: 500; display: block; margin-bottom: 6px;">Mật khẩu hiện tại</label>
                  <div style="display: flex; position: relative;">
                    <input type="password" id="txtCurrentPassword" class="form-input" style="width: 100%; padding: 8px 36px 8px 12px; border: 1px solid #ced4da; border-radius: 4px; box-sizing: border-box;" placeholder="Nhập mật khẩu hiện tại..." />
                    <button type="button" id="toggleCurrentPass" style="position: absolute; right: 10px; top: 50%; transform: translateY(-50%); background: none; border: none; color: #6c757d; cursor: pointer; padding: 4px;"><i class="fa-regular fa-eye"></i></button>
                  </div>
                </div>

                <div class="form-group">
                  <label class="form-label" style="font-size: 12px; font-weight: 500; display: block; margin-bottom: 6px;">Mật khẩu mới</label>
                  <div style="display: flex; position: relative;">
                    <input type="password" id="txtNewPassword" class="form-input" style="width: 100%; padding: 8px 36px 8px 12px; border: 1px solid #ced4da; border-radius: 4px; box-sizing: border-box;" placeholder="Nhập mật khẩu mới..." />
                    <button type="button" id="toggleNewPass" style="position: absolute; right: 10px; top: 50%; transform: translateY(-50%); background: none; border: none; color: #6c757d; cursor: pointer; padding: 4px;"><i class="fa-regular fa-eye"></i></button>
                  </div>
                </div>

                <div class="form-group">
                  <label class="form-label" style="font-size: 12px; font-weight: 500; display: block; margin-bottom: 6px;">Xác nhận mật khẩu mới</label>
                  <div style="display: flex; position: relative;">
                    <input type="password" id="txtConfirmPassword" class="form-input" style="width: 100%; padding: 8px 36px 8px 12px; border: 1px solid #ced4da; border-radius: 4px; box-sizing: border-box;" placeholder="Nhập lại mật khẩu mới..." />
                    <button type="button" id="toggleConfirmPass" style="position: absolute; right: 10px; top: 50%; transform: translateY(-50%); background: none; border: none; color: #6c757d; cursor: pointer; padding: 4px;"><i class="fa-regular fa-eye"></i></button>
                  </div>
                </div>

              </div>
              <div class="app-modal-footer" style="border-top: 1px solid #e9ecef; padding-top: 16px; margin-top: 20px; display: flex; justify-content: flex-end; gap: 8px;">
                <button type="button" id="btnCancelPassword" class="btn btn-outline" style="padding: 6px 16px; border: 1px solid #ced4da; border-radius: 4px; background: #fff; cursor: pointer;">Hủy</button>
                <button type="button" id="btnSavePassword" class="btn btn-primary" style="background-color: #11A75D; color: #fff; border: none; padding: 6px 16px; border-radius: 4px; cursor: pointer;">Cập nhật</button>
              </div>
            </div>
          </div>
        `;
      default:
        return '';
    }
  };

  // Hàm mở Modal: Append vào DOM, gắn event xử lý và hiện lên
  const openModal = (modalId: string) => {
    // Nếu đã tồn tại thì xóa trước để sạch DOM
    const existing = document.getElementById(modalId);
    if (existing) existing.remove();

    // Chèn HTML mới vào body
    const html = getModalHtml(modalId);
    document.body.insertAdjacentHTML('beforeend', html);

    const el = document.getElementById(modalId);
    if (el) {
      el.style.display = 'flex';
      setTimeout(() => el.classList.add('active'), 10);
      bindModalEvents(modalId);
    }
  };

  // Hàm đóng Modal: Remove khỏi DOM khi ẩn xong
  const closeModal = (modalId: string) => {
    const el = document.getElementById(modalId);
    if (el) {
      el.classList.remove('active');
      setTimeout(() => {
        el.remove(); // Xóa hoàn toàn khỏi DOM
      }, 300);
    }
  };

  // Helper ẩn/hiện mật khẩu
  const bindPasswordToggle = (toggleBtnId: string, inputId: string) => {
    const btn = document.getElementById(toggleBtnId);
    const input = document.getElementById(inputId) as HTMLInputElement;
    if (btn && input) {
      btn.addEventListener('click', () => {
        const icon = btn.querySelector('i');
        if (input.type === 'password') {
          input.type = 'text';
          icon?.classList.remove('fa-eye');
          icon?.classList.add('fa-eye-slash');
        } else {
          input.type = 'password';
          icon?.classList.remove('fa-eye-slash');
          icon?.classList.add('fa-eye');
        }
      });
    }
  };

  // Hàm gọi API lấy thông tin profile
  const fetchUserProfile = async () => {
    const currentUser = getUserLogin();
    const userId = currentUser?.id;

    if (!userId) {
      showToast('error', 'Không tìm thấy thông tin định danh người dùng!');
      return;
    }

    const firstNameInput = document.getElementById('profFirstName') as HTMLInputElement;
    const lastNameInput = document.getElementById('profLastName') as HTMLInputElement;
    const usernameInput = document.getElementById('profUsername') as HTMLInputElement;
    const phoneInput = document.getElementById('profPhone') as HTMLInputElement;
    const emailInput = document.getElementById('profEmail') as HTMLInputElement;
    const saveBtn = document.getElementById('btnSaveProfile') as HTMLButtonElement;

    if (saveBtn) saveBtn.disabled = true;
    if (firstNameInput) firstNameInput.value = 'Đang tải...';
    if (lastNameInput) lastNameInput.value = 'Đang tải...';

    try {
      const res: any = await requestApi(`/api/app/user-management/${userId}/identity-user`, 'GET');
      const data = res?.result || res;

      if (data) {
        if (firstNameInput) firstNameInput.value = data.name || '';
        if (lastNameInput) lastNameInput.value = data.surname || '';
        if (usernameInput) usernameInput.value = data.userName || '';
        if (phoneInput) phoneInput.value = data.phoneNumber || '';
        if (emailInput) {
          emailInput.value = data.email || '';
          cachedUserEmail = data.email || '';
        }
      }
    } catch (err) {
      console.error('Lỗi tải thông tin tài khoản:', err);
      showToast('error', 'Không thể tải thông tin cá nhân!');
    } finally {
      if (saveBtn) saveBtn.disabled = false;
    }
  };

  // Gắn sự kiện chi tiết cho từng Modal khi được append
  const bindModalEvents = (modalId: string) => {
    if (modalId === 'accountProfileModal') {
      fetchUserProfile();

      document.getElementById('btnCloseProfileModal')?.addEventListener('click', () => closeModal('accountProfileModal'));
      document.getElementById('btnCancelProfile')?.addEventListener('click', () => closeModal('accountProfileModal'));
      
      document.getElementById('btnOpenChangePassword')?.addEventListener('click', () => {
        openModal('changePasswordModal');
      });

      document.getElementById('btnEditProfPhone')?.addEventListener('click', () => {
        openModal('editPhoneModal');
      });

      document.getElementById('btnEditProfEmail')?.addEventListener('click', () => {
        openModal('editEmailModal');
      });

      document.getElementById('btnSaveProfile')?.addEventListener('click', async () => {
        const currentUser = getUserLogin();
        const userId = currentUser?.id;
        if (!userId) return;

        const firstNameInput = document.getElementById('profFirstName') as HTMLInputElement;
        const lastNameInput = document.getElementById('profLastName') as HTMLInputElement;
        const usernameInput = document.getElementById('profUsername') as HTMLInputElement;
        const saveBtn = document.getElementById('btnSaveProfile') as HTMLButtonElement;

        if (!saveBtn) return;
        const originalHtml = saveBtn.innerHTML;
        saveBtn.disabled = true;
        saveBtn.innerHTML = '<i class="fas fa-spinner fa-spin" style="margin-right: 6px;"></i>Đang lưu...';

        try {
          const payload = {
            name: firstNameInput ? firstNameInput.value.trim() : '',
            surname: lastNameInput ? lastNameInput.value.trim() : '',
            userName: usernameInput ? usernameInput.value.trim() : '',
            appName: "mobile_app"
          };

          await requestApi(`/api/app/user-management/${userId}/my-profile`, 'PUT', payload);
          showToast('success', 'Đã lưu thông tin tài khoản thành công!');
          closeModal('accountProfileModal');
        } catch (err: any) {
          showToast('error', err.message || 'Có lỗi xảy ra khi lưu thông tin!');
        } finally {
          saveBtn.disabled = false;
          saveBtn.innerHTML = originalHtml;
        }
      });
    } 
    else if (modalId === 'editPhoneModal') {
      document.getElementById('btnClosePhoneModal')?.addEventListener('click', () => closeModal('editPhoneModal'));
      document.getElementById('btnCancelPhone')?.addEventListener('click', () => closeModal('editPhoneModal'));

      document.getElementById('btnSavePhone')?.addEventListener('click', async () => {
        const inputNewPhone = document.getElementById('txtNewPhone') as HTMLInputElement;
        const btnSavePhone = document.getElementById('btnSavePhone') as HTMLButtonElement;

        if (!inputNewPhone || !inputNewPhone.value.trim()) {
          showToast('error', 'Vui lòng nhập số điện thoại hợp lệ.');
          return;
        }

        const newPhone = inputNewPhone.value.trim();
        const originalText = btnSavePhone.innerHTML;
        btnSavePhone.disabled = true;
        btnSavePhone.innerHTML = `<i class="fas fa-spinner fa-spin"></i> Đang xử lý...`;

        try {
          const payload = { phoneNumber: newPhone };
          const response: any = await requestApi('/api/app/mobile-auth/profile-phone', 'PUT', payload);
          const res = response?.result || response;

          if (res?.isOtp) {
            closeModal('editPhoneModal');
            OtpPopup.show({
              otpId: res.idOtp,
              onSuccess: () => {
                showToast('success', 'Xác thực và cập nhật số điện thoại thành công!');
                const inputProfPhone = document.getElementById('profPhone') as HTMLInputElement;
                if (inputProfPhone) inputProfPhone.value = newPhone;
              }
            });
          } else {
            showToast('success', 'Cập nhật số điện thoại thành công!');
            const inputProfPhone = document.getElementById('profPhone') as HTMLInputElement;
            if (inputProfPhone) inputProfPhone.value = newPhone;
            closeModal('editPhoneModal');
          }
        } catch (error: any) {
          showToast('error', error.message || 'Có lỗi xảy ra khi cập nhật số điện thoại.');
        } finally {
          btnSavePhone.disabled = false;
          btnSavePhone.innerHTML = originalText;
        }
      });
    }
    else if (modalId === 'editEmailModal') {
      document.getElementById('btnCloseEmailModal')?.addEventListener('click', () => closeModal('editEmailModal'));
      document.getElementById('btnCancelEmail')?.addEventListener('click', () => closeModal('editEmailModal'));

      document.getElementById('btnSaveEmail')?.addEventListener('click', async () => {
        const inputNewEmail = document.getElementById('txtNewEmail') as HTMLInputElement;
        const btnSaveEmail = document.getElementById('btnSaveEmail') as HTMLButtonElement;

        if (!inputNewEmail || !inputNewEmail.value.trim()) {
          showToast('error', 'Vui lòng nhập email hợp lệ.');
          return;
        }
        const EMAIL_REGEX =
          /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;

        const email = inputNewEmail.value.trim();

        if (!email) {
          showToast('error','Vui lòng nhập email.');
          return;
        }

        if (!EMAIL_REGEX.test(email)) {
          showToast('error','Email không hợp lệ. Vui lòng kiểm tra lại.');
          return;
        }
        const newEmail = inputNewEmail.value.trim();
        const originalText = btnSaveEmail.innerHTML;
        btnSaveEmail.disabled = true;
        btnSaveEmail.innerHTML = `<i class="fas fa-spinner fa-spin"></i> Đang xử lý...`;

        try {
          const payload = { email: newEmail };
          const response: any = await requestApi('/api/app/mobile-auth/profile-email', 'PUT', payload);
          const res = response?.result || response;

          if (res?.isOtp) {
            closeModal('editEmailModal');
            OtpPopup.show({
              otpId: res.idOtp,
              onSuccess: () => {
                showToast('success', 'Xác thực và cập nhật email thành công!');
                const inputProfEmail = document.getElementById('profEmail') as HTMLInputElement;
                if (inputProfEmail) inputProfEmail.value = newEmail;
                cachedUserEmail = newEmail;
              }
            });
          } else {
            showToast('success', 'Cập nhật email thành công!');
            const inputProfEmail = document.getElementById('profEmail') as HTMLInputElement;
            if (inputProfEmail) inputProfEmail.value = newEmail;
            cachedUserEmail = newEmail;
            closeModal('editEmailModal');
          }
        } catch (error: any) {
          showToast('error', error.message || 'Có lỗi xảy ra khi cập nhật email.');
        } finally {
          btnSaveEmail.disabled = false;
          btnSaveEmail.innerHTML = originalText;
        }
      });
    }
    else if (modalId === 'changePasswordModal') {
      bindPasswordToggle('toggleCurrentPass', 'txtCurrentPassword');
      bindPasswordToggle('toggleNewPass', 'txtNewPassword');
      bindPasswordToggle('toggleConfirmPass', 'txtConfirmPassword');

      document.getElementById('btnClosePasswordModal')?.addEventListener('click', () => closeModal('changePasswordModal'));
      document.getElementById('btnCancelPassword')?.addEventListener('click', () => closeModal('changePasswordModal'));

      document.getElementById('btnSavePassword')?.addEventListener('click', async () => {
        const inputCurrentPass = document.getElementById('txtCurrentPassword') as HTMLInputElement;
        const inputNewPass = document.getElementById('txtNewPassword') as HTMLInputElement;
        const inputConfirmPass = document.getElementById('txtConfirmPassword') as HTMLInputElement;
        const btnSavePassword = document.getElementById('btnSavePassword') as HTMLButtonElement;

        if (!inputCurrentPass || !inputNewPass || !inputConfirmPass) return;

        const currentPassword = inputCurrentPass.value;
        const newPassword = inputNewPass.value;
        const confirmPassword = inputConfirmPass.value;

        if (!currentPassword) {
            showToast('error', 'Vui lòng nhập mật khẩu hiện tại!');
            return;
        }

        if (!newPassword) {
            showToast('error', 'Vui lòng nhập mật khẩu mới!');
            return;
        }

        if (newPassword.length < 8) {
            showToast('error', 'Mật khẩu mới phải có ít nhất 8 ký tự!');
            return;
        }

        if (!/[A-Z]/.test(newPassword)) {
            showToast('error', 'Mật khẩu mới phải có ít nhất 1 chữ hoa!');
            return;
        }

        if (!/[a-z]/.test(newPassword)) {
            showToast('error', 'Mật khẩu mới phải có ít nhất 1 chữ thường!');
            return;
        }

        if (!/[^a-zA-Z0-9]/.test(newPassword)) {
            showToast('error', 'Mật khẩu mới phải có ít nhất 1 ký tự đặc biệt!');
            return;
        }
        if (!/\d/.test(newPassword)) {
            showToast('error', 'Mật khẩu mới phải có ít nhất 1 số!');
            return;
        }

        if (!confirmPassword) {
            showToast('error', 'Vui lòng nhập lại mật khẩu mới!');
            return;
        }

        if (newPassword !== confirmPassword) {
            showToast('error', 'Mật khẩu xác nhận không khớp!');
            return;
        }

        if (newPassword === currentPassword) {
            showToast('error', 'Mật khẩu mới phải khác mật khẩu hiện tại!');
            return;
        }

        const originalText = btnSavePassword.innerHTML;
        btnSavePassword.disabled = true;
        btnSavePassword.innerHTML = `<i class="fas fa-spinner fa-spin"></i> Đang xử lý...`;

        try {
          const payload = {
            identifier: cachedUserEmail || getUserLogin()?.email || '',
            currentPassword: currentPassword,
            newPassword: newPassword
          };

          const response: any = await requestApi('/api/app/mobile-auth/change-password', 'POST', payload);
          const res = response?.result || response;

          if (res?.isOtp) {
            closeModal('changePasswordModal');
            closeModal('accountProfileModal');

            OtpPopup.show({
              otpId: res.idOtp,
              onSuccess: () => {
                showToast('success', 'Đổi mật khẩu thành công! Vui lòng đăng nhập lại.');
                handleAdminLogout(new Event('click'));
              }
            });
          } else {
            showToast('success', 'Đổi mật khẩu thành công! Vui lòng đăng nhập lại.');
            closeModal('changePasswordModal');
            closeModal('accountProfileModal');
            handleAdminLogout(new Event('click'));
          }
        } catch (error: any) {
          showToast('error', error.message || 'Có lỗi xảy ra khi đổi mật khẩu.');
        } finally {
          btnSavePassword.disabled = false;
          btnSavePassword.innerHTML = originalText;
        }
      });
    }
  };

  // 3. Lắng nghe sự kiện click mở Modal chính từ nút #btnAccountInfor
  document.addEventListener('click', (e: Event) => {
    const target = e.target as HTMLElement;
    const btnAccountInfor = target.closest('#btnAccountInfor');
    
    if (btnAccountInfor) {
      e.preventDefault();
      openModal('accountProfileModal'); // Gọi openModal để tự động append vào DOM và hiển thị
      
      const dropdown = document.getElementById('profileDropdown');
      if (dropdown) dropdown.classList.remove('active');
    }
  });
}